/**
 * ALL user-facing copy for ACH bank-account billing lives in this one file
 * (AchBillingCard + AchBankDialog read it; neither hard-codes a sentence).
 * Edit a string here to change it everywhere.
 *
 * WE WRITE NO MANDATE / LEGAL TEXT. The bank form is Stripe's Payment
 * Element, which displays Stripe's own mandate language and records the
 * acceptance. Do not add authorization wording to this file; if the Element
 * ever stops showing it, that is a product/legal decision, not a copy edit.
 *
 * `debitAmountsExplainer` is the plain-language disclosure of what will be
 * debited, when, and how each amount is worked out (Jake approved 2026-10-20:
 * it covers the 1st/15th invoices AND monthly store charges). It states no
 * figures; the invoice and the Store billing section carry the specifics.
 * Every source named here must be one the code really debits to this bank:
 * distributor_invoicing (1st/15th) and store_month_invoice (monthly), and
 * every component those invoices bill must be named (Jake's 2026-10-20
 * disclosure rule). Since Trello #563 (Jake, 2026-10-28) the 1st/15th
 * invoice bills a Net Checkout order's platform fee and shipping beside its
 * decoration, so both are named. Since Trello #618 it also bills a PAYROLL
 * order's platform fee (Jake, 2026-10-29) and its whole shipping and
 * handling (Jake, 2026-10-30: the distributor owes it, not ThreadFlow;
 * feature_flags.PAYROLL_SHIPPING_BILLED_TO_DISTRIBUTOR is True), so the fee
 * and the shipping and handling are named on both kinds. "Shipping and
 * handling", not "shipping": the line bills carrier + surcharge + handling,
 * and the bare word reads as the carrier's charge alone. Since Trello #621
 * (Jake, 2026-10-29) it also bills a CARD order's share shortfall (the part of
 * ThreadFlow's share an allowance, budget or discount left the card charge
 * too small to carry), so that is named too. Change this copy in the SAME
 * change as either: api/tests/test_invoice_ach_disclosure_names_billed_
 * components.py fails while they disagree. The NET-02 ACH sweep
 * (net_obligation_sweep) collects nothing since #618 and is disabled in
 * code; it stays deliberately NOT named until it is enabled (Jake,
 * 2026-10-20).
 *
 * A card may be saved WITHOUT a bank account first (Jake, 2026-10-20; the
 * server provisions the billing Customer, reversing the #3866 gate). No
 * string here may tell a distributor to set up a bank first.
 *
 * NO CARD-FALLBACK SURCHARGE: NET-06 / BR-11 retired it; no string here may
 * mention one (AchBillingCard.spec.js asserts it).
 */
export const ACH_COPY = {
    cardTitle: "ThreadFlow billing",
    lede:
        "How ThreadFlow can collect what you owe, including invoices issued " +
        "on the 1st and the 15th (decoration, platform fees, and shipping " +
        "and handling on your orders) and monthly store charges. Bank " +
        "transfer (ACH) is the preferred method.",

    badgeActive: "Authorized",
    authorizedOn: "Authorized on",
    badgeNotSetUp: "Not set up",
    badgePending: "Verification pending",
    badgeFailed: "Needs attention",
    badgeUnavailable: "Status unavailable",
    statusUnavailable:
        "We couldn't check your bank account status right now. This is " +
        "not a sign that nothing is set up.",
    statusRetryButton: "Check again",

    notSetUp: "Connect a bank account to authorize ACH debits for your invoices.",
    pending:
        "Your bank account is being verified. This usually takes one to two " +
        "business days. Stripe sends verification instructions to the email " +
        "address on your account.",
    failed:
        "We couldn't verify your bank account. Please set it up again, or " +
        "use a different account.",

    setupButton: "Set up bank account",
    updateButton: "Update bank account",
    setupStartError: "We couldn't start bank account setup. Please try again.",

    dialogTitle: "Connect your bank account",
    dialogIntro:
        "Enter your bank details securely with Stripe. ThreadFlow never sees " +
        "your full account number.",
    debitAmountsExplainer:
        "Once your bank account is connected, ThreadFlow can debit it for " +
        "invoices, which include order invoices and store charges. Order " +
        "invoices are issued on the 1st and the 15th for orders that have " +
        "shipped in full, and the debit for one is the total of that " +
        "invoice. It lists the decoration for each order and, on a Net " +
        "Checkout or payroll deduction order, ThreadFlow's platform fee and " +
        "the order's shipping and handling. On a card order where " +
        "an allowance, budget or discount left the card payment smaller " +
        "than ThreadFlow's share of the order, it also lists the part of " +
        "that share the card payment did not cover. Store charges are " +
        "invoiced once a month, " +
        "after the month closes. When a store-charge invoice is issued, the " +
        "debit is the total of that invoice, worked out from your plan's " +
        "prices and how each of your stores was used that month. See Store " +
        "billing on your Payments page for how stores are counted.",
    loadingForm: "Loading the secure bank form...",
    stripeLoadError:
        "Unable to load our payment processor. Please refresh and try again.",
    submitButton: "Link bank account",
    cancelButton: "Cancel",
    doneButton: "Done",
    confirmError:
        "Something went wrong linking your bank account. Please try again.",
    recordError:
        "Your bank account was linked, but we couldn't save it yet. " +
        "Please try again.",
    notVerifiedError:
        "Your bank account could not be verified. Please try again with " +
        "the same or a different account.",

    canceledBody:
        "This bank account setup expired or was canceled. Close this window " +
        "and choose Set up bank account to start again.",
    successTitle: "Bank account connected",
    successBody:
        "Your bank account is now ThreadFlow's way to collect your invoices.",
    alreadyVerified:
        "Your bank account is already verified. Choose Link bank account to " +
        "finish authorizing it.",
    hostedVerificationLink: "Finish verification on Stripe",
    pendingTitle: "Verification pending",
    pendingBody:
        "Your bank account isn't authorized until verification finishes. " +
        "This usually takes one to two business days.",
};
